// Each kind of reading's colour (cloud ADR 0396, TOM-678; VENDORED.md change
// 15): the path→kind table every renderer shares. A template marks its parts
// with `data-domain` from it when it writes a page, and the frame's base style
// gives each kind its `--tc-<kind>` tokens (page.js, from tokens.json). The
// web's directly drawn panels (TOM-679) and the iPhone (TOM-680) read the same
// table; `tests/page-domains.fixture.json` holds the cases they check theirs
// against.
//
// A kind is the reading's identity, never its health: a green battery line
// can sit under a battery alarm. A path of no kind (the anchor watch, AIS, a
// switch, an unknown sensor) draws in ink with the coral accent. In Night
// every kind is the one red.

/** The kinds of reading, in tokens.json's order. */
export const PAGE_DOMAINS = Object.freeze([
  "power",
  "water",
  "fuel",
  "weather",
  "wind",
  "depth",
  "navigation",
  "engine",
]);

/**
 * The table, first match wins: a Signal K path's pattern and its kind. Fuel
 * rate is fuel before the engine claims the rest of `propulsion.*`; the anchor
 * is no kind before navigation claims the rest of `navigation.*`.
 */
export const PAGE_DOMAIN_RULES = Object.freeze([
  [/^electrical\./, "power"],
  [/^tanks\.(freshWater|wasteWater|blackWater|grayWater|greyWater)\./, "water"],
  [/^tanks\.fuel\./, "fuel"],
  [/^propulsion\.[^.]+\.fuel\./, "fuel"],
  [/^propulsion\./, "engine"],
  [/^environment\.(outside|inside)\./, "weather"],
  [/^environment\.wind\./, "wind"],
  [/^environment\.(depth|water|tide|current)\./, "depth"],
  [/^navigation\.anchor\./, null],
  [/^navigation\./, "navigation"],
  [/^steering\./, "navigation"],
  [/^performance\./, "navigation"],
]);

/** A path's kind, or null for a path of no kind. */
export function pageDomainFor(path) {
  if (typeof path !== "string") return null;
  for (const [pattern, domain] of PAGE_DOMAIN_RULES)
    if (pattern.test(path)) return domain;
  return null;
}

/**
 * Each module kind's colour, as TOM-679's module cards draw it: the anchor
 * watch, AIS, the stereo, the changes list and a camera have none. A module's rows of
 * other kinds (the reserves' house bank, the tanks' fuel) take their own.
 */
export const MODULE_DOMAINS = Object.freeze({
  "anchor-watch": null,
  "anchor-details": null,
  "environment-outlook": "weather",
  forecast: "weather",
  tide: "depth",
  pressure: "weather",
  "power-budget": "power",
  "tanks-budget": "water",
  "nearby-vessels": null,
  stereo: null,
  helm: "navigation",
  "depth-safety": "depth",
  changes: null,
  "engine-vitals": "engine",
  reserves: "fuel",
  traffic: null,
  passage: "navigation",
  "set-drift": "depth",
  "route-map": "navigation",
  camera: null,
});

/** A module kind's colour, or null. */
export function pageDomainForModule(kind) {
  return Object.hasOwn(MODULE_DOMAINS, kind) ? MODULE_DOMAINS[kind] : null;
}
